/**
 * Integer codes for company.industryId.
 * Single source of truth — do not duplicate this list in views.
 */

export const IndustryType = Object.freeze({
  AD_AGENCY_OR_MARKETING: 1,
  AGRICULTURE_FARMERS_RANCHERS: 2,
  ARCHITECTURE_ENGINEERING: 3,
  BEAUTY_PARLOUR_HAIR_SALON: 4,
  CATERING_RESTAURANTS_BAR: 5,
  CHURCHES_RELIGIOUS: 6,
  CONSTRUCTION: 7,
  FINANCIAL_ACCOUNTING: 8,
  FINANCIAL_SERVICES: 9,
  FREELANCE_DEALER_SALES: 10,
  GENERAL_PRODUCT_BASED: 11,
  GENERAL_SERVICE_BASED: 12,
  HVAC_ELECTRICIAN_PLUMBER: 13,
  HEALTH_SERVICES_PHARMACY: 14,
  HOME_ASSOCIATION_PROPERTY: 15,
  HOTEL_MOTEL: 16,
  IT_SOFTWARE_HARDWARE: 17,
  IT_CONSULTING: 18,
  INSURANCE: 19,
  LAWN_LANDSCAPE: 20,
  LAWYERS_LEGAL: 21,
  MANUFACTURE_COMPANIES: 22,
  MANUFACTURER_AGENTS: 23,
  NON_PROFIT: 24,
  PACKERS_MOVERS_TRANSPORT: 25,
  PHOTOGRAPHERS_ARTISTS_WRITERS: 26,
  PROFESSIONAL_CONSULTING: 27,
  REAL_ESTATE_AGENT: 28,
  RENTAL_RESIDENTIAL_COMMERCIAL: 29,
  REPAIRS_MAINTENANCE: 30,
  RETAIL_ONLINE_STORE: 31,
  VEHICLES_SHOWROOM: 32,
  WHOLESALE_DISTRIBUTION: 33,
});

export const INDUSTRY_TYPE_LABELS = Object.freeze({
  [IndustryType.AD_AGENCY_OR_MARKETING]: 'Ad Agency or Marketing',
  [IndustryType.AGRICULTURE_FARMERS_RANCHERS]: 'Agriculture , Farmers & Ranchers',
  [IndustryType.ARCHITECTURE_ENGINEERING]: 'Architecture / Engineering Services',
  [IndustryType.BEAUTY_PARLOUR_HAIR_SALON]: 'Beauty Parlour/ Hair saloon shops',
  [IndustryType.CATERING_RESTAURANTS_BAR]: 'Caterings / Delivery or Restaurants & Bar',
  [IndustryType.CHURCHES_RELIGIOUS]: 'Churches and Other Religious Organizations',
  [IndustryType.CONSTRUCTION]: 'Construction Agents / Companies',
  [IndustryType.FINANCIAL_ACCOUNTING]: 'Financial Accounting Services',
  [IndustryType.FINANCIAL_SERVICES]: 'Financial Services',
  [IndustryType.FREELANCE_DEALER_SALES]: 'Freelance Service / Dealer - Sales',
  [IndustryType.GENERAL_PRODUCT_BASED]: 'General Product Based Business',
  [IndustryType.GENERAL_SERVICE_BASED]: 'General Service Based Business',
  [IndustryType.HVAC_ELECTRICIAN_PLUMBER]: 'HVAC , Electrician & plumber etc services',
  [IndustryType.HEALTH_SERVICES_PHARMACY]: 'Health Services / Pharmacy',
  [IndustryType.HOME_ASSOCIATION_PROPERTY]: 'Home Association or Property Managament Services',
  [IndustryType.HOTEL_MOTEL]: 'Hotel /Motel',
  [IndustryType.IT_SOFTWARE_HARDWARE]: 'IT Software / Hardware',
  [IndustryType.IT_CONSULTING]: 'ITConsulting',
  [IndustryType.INSURANCE]: 'Insurance Agents / Companies',
  [IndustryType.LAWN_LANDSCAPE]: 'Lawn Service & Landsca Maintenance',
  [IndustryType.LAWYERS_LEGAL]: 'Lawyers or Legal Advisory Services',
  [IndustryType.MANUFACTURE_COMPANIES]: 'Manufacture Companies',
  [IndustryType.MANUFACTURER_AGENTS]: 'Manufacturer Agents or Dealers',
  [IndustryType.NON_PROFIT]: 'Non Profit Organization',
  [IndustryType.PACKERS_MOVERS_TRANSPORT]: 'Packers & Movers or Transport Services',
  [IndustryType.PHOTOGRAPHERS_ARTISTS_WRITERS]: 'Photographers , Artists or writers',
  [IndustryType.PROFESSIONAL_CONSULTING]: 'Professional Consulting',
  [IndustryType.REAL_ESTATE_AGENT]: 'Real Estate Agent / Broker',
  [IndustryType.RENTAL_RESIDENTIAL_COMMERCIAL]: 'Rental - Residential or Commercial',
  [IndustryType.REPAIRS_MAINTENANCE]: 'Repairs & Maintenance',
  [IndustryType.RETAIL_ONLINE_STORE]: 'Retail & Online store Business',
  [IndustryType.VEHICLES_SHOWROOM]: 'Vehicles showroom-Sales',
  [IndustryType.WHOLESALE_DISTRIBUTION]: 'Wholesale Sales and Distribution',
});

/** Industries that unlock consultant-oriented invoice / services UI. */
export const CONSULTANT_INDUSTRY_IDS = Object.freeze([
  IndustryType.ARCHITECTURE_ENGINEERING,
  IndustryType.FINANCIAL_ACCOUNTING,
  IndustryType.FREELANCE_DEALER_SALES,
  IndustryType.GENERAL_SERVICE_BASED,
  IndustryType.HVAC_ELECTRICIAN_PLUMBER,
  IndustryType.HEALTH_SERVICES_PHARMACY,
  IndustryType.IT_SOFTWARE_HARDWARE,
  IndustryType.IT_CONSULTING,
  IndustryType.LAWYERS_LEGAL,
  IndustryType.PHOTOGRAPHERS_ARTISTS_WRITERS,
  IndustryType.PROFESSIONAL_CONSULTING,
]);

export const INDUSTRY_TYPES_FOR_SELECT = Object.freeze(
  Object.entries(INDUSTRY_TYPE_LABELS)
    .map(([value, label]) => ({
      id: Number(value),
      value: Number(value),
      label,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
);

export function getIndustryTypeLabel(industryId, { fallback = 'Not Specified', consultantAsLabel = false } = {}) {
  if (industryId === '' || industryId === null || industryId === undefined) {
    return fallback;
  }
  const key = Number(industryId);
  if (!Number.isInteger(key) || key < 1) {
    return fallback;
  }
  const label = INDUSTRY_TYPE_LABELS[key];
  if (!label) return fallback;
  if (consultantAsLabel && key === IndustryType.IT_CONSULTING) {
    return 'Consultant';
  }
  return label;
}

export function isValidIndustryTypeId(industryId) {
  const key = Number(industryId);
  return Number.isInteger(key) && Object.prototype.hasOwnProperty.call(INDUSTRY_TYPE_LABELS, key);
}

export function isConsultantIndustry(industryId) {
  const key = Number(industryId);
  if (!Number.isInteger(key)) return false;
  return CONSULTANT_INDUSTRY_IDS.includes(key);
}
